/**
 * 4.40 RS — return path through the web paywall.
 *
 * A signed-in free user who opens `/r/{code}` is sent to `/upgrade`; checkout is a
 * full-page trip to start.kidneypal.app that comes back with `?purchased=1`, so
 * router state can't carry the destination. `RequirePremium` saves the path here
 * before either `/upgrade` redirect, and the Gate takes it once access is granted.
 *
 * Only a share path (`/r/` + an 8-character Crockford code) is ever stored or
 * returned, so this can never become an open redirect. Entries expire after 1 h,
 * `take` is take-once, and every storage access is guarded (private mode, blocked
 * storage).
 */

export const RETURN_PATH_KEY = 'kp_return_path'
export const RETURN_PATH_TTL_MS = 60 * 60 * 1000

const RETURN_PATH_RE = /^\/r\/[0-9A-HJKMNP-TV-Z]{8}$/

/** True for a path this module may store / return. */
export function isAllowedReturnPath(path: unknown): path is string {
  return typeof path === 'string' && RETURN_PATH_RE.test(path)
}

/** Remember `path` for after checkout. Anything but a share path is ignored. */
export function saveReturnPath(path: string, now: number = Date.now()): void {
  if (!isAllowedReturnPath(path)) return
  try {
    localStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, at: now }))
  } catch {
    // Storage unavailable — the user lands on /diary, as before 4.40.
  }
}

/** The stored share path (once), or null when missing, expired or not allowed. */
export function takeReturnPath(now: number = Date.now()): string | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(RETURN_PATH_KEY)
    if (raw !== null) localStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as { path?: unknown; at?: unknown }
    const at = typeof value?.at === 'number' ? value.at : NaN
    if (!isAllowedReturnPath(value?.path) || !Number.isFinite(at)) return null
    if (now - at > RETURN_PATH_TTL_MS || at - now > RETURN_PATH_TTL_MS) return null
    return value.path
  } catch {
    return null
  }
}
